import React from "react";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Input } from "antd";
import dayjs from "dayjs";
import styled from "styled-components";
import { AppDatePicker, DynamicModal } from "@/shared/components";
import { Icon } from "@/shared/components/Icon/Icon";
import { DeliverableTypeSelect } from "../detail/DeliverableTypeSelect";
import { InlineError, RequiredMark } from "./SectionPrimitives";
import type { EngagementDeliverablesController } from "./types";

const ModalFields = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin-top: 8px;
`;

const ModalField = styled.div<{ $invalid?: boolean }>`
	display: flex;
	flex-direction: column;
	gap: 5px;
	color: #707070;
	font-size: 13px;

	.ant-input,
	.ant-select-selector,
	.app-date-picker {
		width: 100%;
		height: 40px !important;
		border-color: ${(props) => (props.$invalid ? "#f72586" : "#cdd4f6")} !important;
		border-radius: 8px !important;
		box-shadow: none !important;
	}

	.ant-select-selector {
		display: flex !important;
		align-items: center !important;
	}

	.app-date-picker {
		font-size: 14px;
	}
`;

interface Props {
	editor: EngagementDeliverablesController;
}

export const EngagementTermsEditorModals: React.FC<Props> = ({ editor }) => (
	<>
		<DynamicModal
			open={!!editor.deliverableModal}
			onCancel={() => editor.setDeliverableModal(null)}
			title={editor.deliverableModal?.mode === "edit" ? "Edit Deliverable" : "Add Deliverable"}
			icon={
				editor.deliverableModal?.mode === "edit" ? (
					<EditOutlined style={{ color: "#3a0ca3", fontSize: 32 }} />
				) : (
					<PlusOutlined style={{ color: "#3a0ca3", fontSize: 32 }} />
				)
			}
			confirmText={editor.deliverableModal?.mode === "edit" ? "Save" : "Add"}
			onConfirm={editor.confirmDeliverableModal}
			width={720}
		>
			<ModalFields>
				<ModalField $invalid={editor.deliverableDraftErrors.has("name")}>
					<span>
						Title<RequiredMark aria-hidden="true">*</RequiredMark>
					</span>
					<Input
						aria-label="Title"
						value={editor.deliverableDraft.name}
						onChange={(event) => editor.updateDeliverableDraft("name", event.target.value)}
					/>
					{editor.deliverableDraftErrors.has("name") && <InlineError>Title is required</InlineError>}
				</ModalField>
				<ModalField $invalid={editor.deliverableDraftErrors.has("deliverableTypes")}>
					<span>
						Deliverable Type<RequiredMark aria-hidden="true">*</RequiredMark>
					</span>
					<DeliverableTypeSelect
						aria-label="Deliverable Type"
						value={editor.deliverableDraft.deliverableTypes}
						onChange={(deliverableTypes) =>
							editor.updateDeliverableDraft("deliverableTypes", deliverableTypes)
						}
					/>
					{editor.deliverableDraftErrors.has("deliverableTypes") && (
						<InlineError>Select at least one deliverable type</InlineError>
					)}
				</ModalField>
				<ModalField $invalid={editor.deliverableDraftErrors.has("dueDate")}>
					<span>
						Deliverable Due Date<RequiredMark aria-hidden="true">*</RequiredMark>
					</span>
					<AppDatePicker
						aria-label="Deliverable Due Date"
						value={editor.deliverableDraft.dueDate ? dayjs(editor.deliverableDraft.dueDate) : null}
						format="MMM D, YYYY"
						suffixIcon={<Icon src="/icon/cet-calendar.svg" size={18} preserveOriginalColor />}
						onChange={(date) => editor.updateDeliverableDraft("dueDate", date?.format("YYYY-MM-DD") || "")}
					/>
					{editor.deliverableDraftErrors.has("dueDate") && <InlineError>Due date is required</InlineError>}
				</ModalField>
			</ModalFields>
		</DynamicModal>

		<DynamicModal
			open={editor.goLiveEditingId != null}
			onCancel={() => editor.setGoLiveEditingId(null)}
			title="Edit Go Live"
			icon={<EditOutlined style={{ color: "#3a0ca3", fontSize: 32 }} />}
			confirmText="Save"
			onConfirm={editor.confirmGoLiveModal}
			width={440}
		>
			<ModalFields>
				<ModalField $invalid={!!editor.goLiveError}>
					<span>
						Live Date<RequiredMark aria-hidden="true">*</RequiredMark>
					</span>
					<AppDatePicker
						aria-label="Live Date"
						value={editor.goLiveDraft ? dayjs(editor.goLiveDraft) : null}
						format="MMM D, YYYY"
						disabledDate={(date) => {
							const finalDeliverable = editor.deliverables[editor.deliverables.length - 1];
							return !!finalDeliverable?.dueDate && date.isBefore(dayjs(finalDeliverable.dueDate), "day");
						}}
						suffixIcon={<Icon src="/icon/cet-calendar.svg" size={18} preserveOriginalColor />}
						onChange={(date) => editor.updateGoLiveDraft(date?.format("YYYY-MM-DD") || "")}
					/>
					{editor.goLiveError && <InlineError>{editor.goLiveError}</InlineError>}
				</ModalField>
			</ModalFields>
		</DynamicModal>
	</>
);
